import React, { useState, useEffect } from 'react';
import { API } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import InviteMemberModal from './InviteMemberModal';
import TransferOwnershipModal from './TransferOwnershipModal';
import './MembersSection.css';

function MembersSection({ organizationId }) {
    const { canManage } = usePermissions();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transferTarget, setTransferTarget] = useState(null);

    useEffect(() => {
        loadMembers();
    }, [organizationId]);

    const loadMembers = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await API.getOrganizationMembers(organizationId);
            if (response && response.data) {
                setMembers(response.data.members || []);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChangeRole = async (memberId, newRoleId) => {
        // RÈGLE SPÉCIALE : Si le nouveau rôle est "Propriétaire" (role_id = 1)
        // Afficher un modal de confirmation
        if (newRoleId === 1) {
            const member = members.find(m => m.id === memberId);
            setTransferTarget({
                memberId,
                memberName: `${member.first_name} ${member.last_name}`
            });
            setShowTransferModal(true);
            setEditingMember(null);
            return;
        }

        // Cas normal : changement de rôle standard
        try {
            await API.updateMemberRole(organizationId, memberId, newRoleId);
            await loadMembers();
            setEditingMember(null);
        } catch (error) {
            alert(error.message);
        }
    };

    const handleConfirmTransfer = async () => {
        if (!transferTarget) return;

        try {
            const response = await API.updateMemberRole(organizationId, transferTarget.memberId, 1);

            // Afficher le message de succès avec avertissement
            if (response && response.data && response.data.warning) {
                alert(`${response.data.message}\n\n⚠️ ${response.data.warning}`);
            }

            await loadMembers();
            setShowTransferModal(false);
            setTransferTarget(null);
        } catch (error) {
            alert(error.message);
        }
    };

    const handleCancelTransfer = () => {
        setShowTransferModal(false);
        setTransferTarget(null);
    };

    const handleRemoveMember = async (memberId, memberName) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${memberName} de l'organisation ?`)) {
            return;
        }

        try {
            await API.removeMember(organizationId, memberId);
            await loadMembers();
        } catch (error) {
            alert(error.message);
        }
    };

    const handleInviteSuccess = () => {
        setShowInviteModal(false);
        loadMembers();
    };

    const roles = API.getOrganizationRoles();

    if (loading) {
        return <div className="members-loading">Chargement des membres...</div>;
    }

    if (error) {
        return <div className="members-error">Erreur : {error}</div>;
    }

    return (
        <div className="members-section">
            <div className="members-header">
                <h2>Membres de l'organisation ({members.length})</h2>
                {canManage && (
                    <button className="btn-primary" onClick={() => setShowInviteModal(true)}>
                        Inviter un membre
                    </button>
                )}
            </div>

            <div className="members-table-container">
                <table className="members-table">
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Email</th>
                            <th>Rôle</th>
                            <th>Membre depuis</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {members.map(member => (
                            <tr key={member.id}>
                                <td>
                                    <div className="member-name">
                                        {member.first_name} {member.last_name}
                                    </div>
                                </td>
                                <td>{member.email}</td>
                                <td>
                                    {editingMember === member.id ? (
                                        <select
                                            className="role-select"
                                            defaultValue={member.role_id}
                                            onChange={(e) => handleChangeRole(member.id, parseInt(e.target.value))}
                                            onBlur={() => setEditingMember(null)}
                                            autoFocus
                                        >
                                            {roles.map(role => (
                                                <option key={role.id} value={role.id}>
                                                    {role.label}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span
                                            className={`role-badge role-${member.role_name}`}
                                            onClick={canManage ? () => setEditingMember(member.id) : undefined}
                                            style={{ cursor: canManage ? 'pointer' : 'default' }}
                                            title={canManage ? "Cliquer pour modifier" : undefined}
                                        >
                                            {member.role_label}
                                        </span>
                                    )}
                                </td>
                                <td>{new Date(member.joined_at).toLocaleDateString('fr-FR')}</td>
                                <td>
                                    {canManage && (
                                        <button
                                            className="btn-danger-small"
                                            onClick={() => handleRemoveMember(member.id, `${member.first_name} ${member.last_name}`)}
                                            title="Supprimer ce membre"
                                        >
                                            Supprimer
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showInviteModal && (
                <InviteMemberModal
                    organizationId={organizationId}
                    onClose={() => setShowInviteModal(false)}
                    onSuccess={handleInviteSuccess}
                />
            )}

            {showTransferModal && transferTarget && (
                <TransferOwnershipModal
                    isOpen={showTransferModal}
                    memberName={transferTarget.memberName}
                    onConfirm={handleConfirmTransfer}
                    onCancel={handleCancelTransfer}
                />
            )}
        </div>
    );
}

export default MembersSection;
